import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Phone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { POST_BY_SLUG, BLOG_POSTS } from "@/lib/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const post = POST_BY_SLUG[slug];

  useSeo({
    title: post ? `${post.title} | AeroAcres Blog` : "Article not found",
    description: post?.description || "",
    path: post ? `/blog/${post.slug}/` : "/blog/",
    image: post?.cover,
  });

  if (!post) return <Navigate to="/blog/" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background" data-testid={`blog-post-${post.slug}`}>
      <Header />

      <section className="pt-[110px] pb-10 bg-muted/40 border-b border-border">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <Breadcrumbs items={[{ label: "Blog", href: "/blog/" }, { label: post.title }]} />
          <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readMinutes} min read</span>
          </div>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl text-primary font-bold leading-tight tracking-tight">
            {post.title}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">{post.description}</p>
        </div>
      </section>

      <section className="py-10 bg-background">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <img src={post.cover} alt={post.coverAlt} loading="lazy" className="w-full h-72 md:h-96 object-cover rounded-3xl border border-border" />
        </div>
      </section>

      <article className="pb-16 bg-background">
        <div className="max-w-3xl mx-auto px-5 md:px-8 space-y-10">
          {post.body.map((b, i) => (
            <section key={i}>
              <h2 className="font-display text-2xl md:text-3xl text-primary font-bold leading-tight">{b.h}</h2>
              {b.p.map((para, j) => (
                <p key={j} className="mt-4 text-foreground/85 leading-relaxed">{para}</p>
              ))}
            </section>
          ))}
        </div>
      </article>

      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">Need a drone spray quote?</h2>
          <p className="mt-4 text-primary-foreground/80">Free, no-obligation quote within 24 hours.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="rounded-full bg-accent hover:bg-accent/90 h-12 px-7 text-base font-semibold">
              <Link to="/#contact">Request a quote <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10 h-12 px-7 text-base font-semibold">
              <a href={`tel:${BUSINESS.phoneRaw}`} data-conversion="phone-call" className="track-phone-click">
                <Phone className="h-4 w-4 mr-1.5" /> Call {BUSINESS.phone}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-muted/40">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <h2 className="font-display text-2xl text-primary font-bold mb-6">Keep reading</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}/`} className="group block p-5 rounded-2xl bg-card border border-border hover:border-accent/40 hover:shadow-card-hover transition-all">
                  <div className="text-xs uppercase tracking-wider text-accent font-semibold">{p.readMinutes} min read</div>
                  <div className="mt-2 font-display text-lg font-bold text-primary group-hover:text-accent transition-colors leading-tight">{p.title}</div>
                  <div className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.description}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
